import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { AppDrawer } from '@/components/navigation/AppDrawer'
import { AppSidebar } from '@/components/navigation/AppSidebar'
import { AppTopBar } from '@/components/navigation/AppTopBar'
import { PublicHeader } from '@/components/navigation/PublicHeader'
import { ConsentBanner } from '@/components/consent/ConsentBanner'
import { WorkspaceFooter } from '@/components/layout/WorkspaceFooter'
import styles from './AppShell.module.css'

type AppShellProps = {
  children: ReactNode
  /** "admin" tones the HUD (data-surface=admin token variant) and drops the
   *  scan-line overlay for dense admin pages. Per-page opt-in lands in Wave 2. */
  surface?: 'default' | 'admin'
}

// localStorage key for the persisted desktop sidebar collapse preference.
const SIDEBAR_COLLAPSED_KEY = 'trc.sidebar.collapsed'

export function AppShell({ children, surface = 'default' }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Desktop sidebar collapse — read synchronously on first render so the
  // rail + content offset paint in the right state (no expand→collapse flash).
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    } catch {
      return false
    }
  })
  const { pathname } = useLocation()

  // Auto-close the drawer on route change so the user doesn't have
  // to manually dismiss it after tapping a nav link. See UX brief
  // docs/ux/2026-05-16-workspace-luxury-redesign.md §4.6.
  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  // Persist the collapse preference so it holds across pages and reloads.
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed))
    } catch {
      // Private mode / disabled storage — state still works this session.
    }
  }, [sidebarCollapsed])

  // --sidebar-w drives the desktop main margin + the wide schedule's
  // full-bleed width, so collapsing the rail lets both reclaim the space.
  const shellStyle = {
    '--sidebar-w': sidebarCollapsed ? '64px' : '240px',
  } as CSSProperties

  return (
    <div
      className={styles.shell}
      style={shellStyle}
      data-surface={surface === 'admin' ? 'admin' : undefined}
    >
      <a href="#main-content" className={styles.skipLink}>
        Skip to content
      </a>
      {surface !== 'admin' ? <div className={styles.scanlines} aria-hidden="true" /> : null}
      {/* Desktop (>=1024): the persistent top bar coexists with the sidebar (the
          inverted-L). It owns the brand + outward links + account; the sidebar
          below owns the operational nav. The slot hides it on phone/tablet,
          where AppTopBar + the merged AppDrawer take over. See
          docs/ux/2026-07-18-nav-top-plus-sidebar.md §6-§7. */}
      <div className={styles.topHeaderSlot}>
        <PublicHeader mode="app" />
      </div>
      <div className={styles.sidebarSlot}>
        <AppSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
        />
      </div>
      <div className={styles.topBarSlot}>
        <AppTopBar onMenuOpen={() => setDrawerOpen(true)} />
      </div>
      <AppDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <main id="main-content" className={`container ${styles.main}`}>{children}</main>
      <WorkspaceFooter />
      <ConsentBanner />
    </div>
  )
}
